/**
 * Extension Intelligence — shared contracts (server, console, tests).
 * See docs/design/extension-intelligence-v1.md. Pure data; no I/O.
 */

import { SEVERITIES, type Severity } from '../types.js';
import type { ExtensionVerdict, ScanFinding } from './scan-types.js';

/** A Chromium extension ID: 32 characters a–p. */
export const EXTENSION_ID_RE = /^[a-p]{32}$/;

/** What a device reports about one installed extension at one version (from chrome.management). */
export interface ExtensionSnapshot {
  name: string;
  version: string;
  permissions: string[];
  hostPermissions: string[];
  installType: 'admin' | 'development' | 'normal' | 'sideload' | 'other';
  updateUrl: string | null;
  homepageUrl: string | null;
}

/** Where an extension comes from. Derived from the update URL, refined by store lookups. */
export const EXTENSION_SOURCES = ['edge_store', 'chrome_store', 'self_hosted', 'unpacked', 'unknown'] as const;
export type ExtensionSource = (typeof EXTENSION_SOURCES)[number];

/**
 * Store listing status.
 *  listed          found in its store
 *  removed         was listed before and is now gone (only ever set after a previous `listed`)
 *  not_found       store says it does not exist and it was never seen listed
 *  not_applicable  not a store extension (self-hosted / unpacked)
 *  unknown         not checked, or the lookup failed
 */
export const STORE_STATUSES = ['listed', 'removed', 'not_found', 'not_applicable', 'unknown'] as const;
export type StoreStatus = (typeof STORE_STATUSES)[number];

/** One store lookup result (a catalog provider's output, normalised). */
export interface StoreInfo {
  source: ExtensionSource;
  status: StoreStatus;
  /** Publisher / developer display name as the store shows it. */
  publisher: string | null;
  /** Stable publisher identifier when the store exposes one. */
  publisherId: string | null;
  publisherVerified: boolean | null;
  listingName: string | null;
  listingVersion: string | null;
  homepageUrl: string | null;
  privacyPolicyUrl: string | null;
  storeUrl: string | null;
}

export const EXTENSION_CHANGE_KINDS = [
  'first_seen',
  'version_changed',
  'version_downgraded',
  'name_changed',
  'permissions_added',
  'permissions_removed',
  'host_permissions_added',
  'host_permissions_removed',
  'update_url_changed',
  'homepage_changed',
  'install_type_changed',
  'publisher_changed',
  'store_status_changed',
  'privacy_policy_changed',
  'store_homepage_changed',
  'risk_increased',
  'risk_decreased',
  'approval_changed',
  /** Package scan verdict changed (Phase 2, docs/design/security-roadmap-v1.md "A2"). details.from/to = verdicts. */
  'verdict_changed',
] as const;
export type ExtensionChangeKind = (typeof EXTENSION_CHANGE_KINDS)[number];

export const CHANGE_SEVERITIES = ['info', 'low', 'medium', 'high', 'critical'] as const;
export type ChangeSeverity = (typeof CHANGE_SEVERITIES)[number];

/** One detected change, human-explainable. */
export interface ExtensionChange {
  kind: ExtensionChangeKind;
  severity: ChangeSeverity;
  /** Short human text, e.g. "Added permissions: cookies" or "Publisher changed: Acme Ltd → Shady LLC". */
  summary: string;
  details: { added?: string[]; removed?: string[]; from?: string | null; to?: string | null };
}

/** Administrator review status of an extension in the organisation (console, rules, Edge policy). */
export const EXTENSION_STATUSES = ['approved', 'unreviewed', 'review', 'restricted', 'blocked'] as const;
export type ExtensionStatus = (typeof EXTENSION_STATUSES)[number];

export const EXTENSION_RISK_LEVELS = SEVERITIES;
export type ExtensionRiskLevel = Severity;

/** One scoring factor: shown to administrators verbatim. `code` is stable for tests/filters. */
export interface RiskFactor {
  code: string;
  points: number;
  text: string;
}

export interface ExtensionRisk {
  score: number; // 0..100
  level: ExtensionRiskLevel;
  factors: RiskFactor[];
}

export interface RiskInput {
  snapshot: ExtensionSnapshot;
  store: StoreInfo | null;
  /** Changes detected in the recent window (the scorer decides which kinds matter; caller passes the last 30 days). */
  recentChanges: ExtensionChange[];
  /**
   * Package scan of the current version (Phase 2), when one exists. Optional: callers without a scan score exactly
   * as before. Only the verdict scores (`scan.known_malicious` / `scan.suspicious` / `scan.elevated`); findings
   * are used for the factor text.
   */
  scan?: { verdict: ExtensionVerdict; findings: ScanFinding[] } | null;
}
